import { useEffect, useState } from "react";
import { Sparkles, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const CONF = {
  "høg": { label: "Høg", cls: "status-ok" },
  middels: { label: "Middels", cls: "status-upcoming" },
  "låg": { label: "Låg", cls: "status-overdue" },
};

export const ConfidenceChip = ({ level }) => {
  const c = CONF[(level || "").toLowerCase()];
  if (!c) return null;
  return (
    <span className={cn("rounded-full border px-1.5 py-0 text-[10px] font-semibold", c.cls)} data-testid={`confidence-${level}`}>
      {c.label}
    </span>
  );
};

/**
 * Generic AI suggestion card: list of proposed field values the user can accept individually.
 * items: [{ key, label, value, display?, confidence? , defaultChecked? }]
 */
export const AiSuggestionCard = ({ title = "AI-forslag – kontroller", subtitle, items = [], onApply, onIgnore, children, testid = "ai-suggestion-card", applyLabel = "Bruk valde" }) => {
  const [sel, setSel] = useState({});
  useEffect(() => {
    const init = {};
    items.forEach((it) => {
      init[it.key] = it.defaultChecked !== undefined ? it.defaultChecked : it.value !== null && it.value !== undefined && it.value !== "";
    });
    setSel(init);
  }, [items]);

  const selectedKeys = Object.keys(sel).filter((k) => sel[k]);
  const allKeys = items.filter((it) => it.value !== null && it.value !== undefined && it.value !== "").map((it) => it.key);

  return (
    <div className="ai-card p-4" data-testid={testid}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{title}</span>
          <Badge variant="outline" className="border-primary/40 text-[10px] uppercase tracking-wider">AI</Badge>
        </div>
        <button type="button" onClick={onIgnore} data-testid={`${testid}-ignore`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <X className="h-3.5 w-3.5" /> Ignorer
        </button>
      </div>
      {subtitle && <p className="mb-3 text-xs text-muted-foreground">{subtitle}</p>}

      {items.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {items.map((it) => {
            const empty = it.value === null || it.value === undefined || it.value === "";
            return (
              <label
                key={it.key}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-lg border bg-card p-2.5",
                  empty && "cursor-default opacity-50"
                )}
              >
                <Checkbox
                  checked={!!sel[it.key]}
                  disabled={empty}
                  onCheckedChange={(v) => setSel((s) => ({ ...s, [it.key]: !!v }))}
                  data-testid={`${testid}-check-${it.key}`}
                  className="mt-0.5"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {it.label} <ConfidenceChip level={it.confidence} />
                  </span>
                  <span className="block truncate text-sm font-medium" title={String(it.display ?? it.value ?? "")}>
                    {empty ? "Ikkje funne" : (it.display ?? String(it.value))}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}

      {children}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {items.length > 0 && selectedKeys.length !== allKeys.length && (
          <Button type="button" size="sm" variant="ghost" onClick={() => onApply(allKeys)} data-testid={`${testid}-apply-all`}>
            Bruk alle
          </Button>
        )}
        <Button type="button" size="sm" onClick={() => onApply(selectedKeys)} disabled={items.length > 0 && selectedKeys.length === 0} data-testid={`${testid}-apply`}>
          <Check /> {applyLabel}{items.length > 0 ? ` (${selectedKeys.length})` : ""}
        </Button>
      </div>
    </div>
  );
};
